import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider, 
  OAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

// Your web app's Firebase configuration
// Replace with your own Firebase config
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Auth providers
const googleProvider = new GoogleAuthProvider();
const microsoftProvider = new OAuthProvider('microsoft.com');

// Configure Google Provider with proper settings
googleProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
googleProvider.addScope('https://www.googleapis.com/auth/userinfo.profile');
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Function to create or update a user document in Firestore
export const createOrUpdateUserDocument = async (user: User) => {
  if (!user) return;
  
  try {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      // Create a new user document
      console.log(`Creating new user document for ${user.email}`);
      await setDoc(userRef, {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp()
      });
      console.log(`User document created for ${user.email}`);
    } else {
      // Update the existing user document
      console.log(`Updating user document for ${user.email}`);
      await updateDoc(userRef, {
        lastLogin: serverTimestamp()
      });
    }
    
    return userRef;
  } catch (error) {
    console.error('Error creating/updating user document:', error);
    throw error;
  }
};

// Auth helper functions

export const signInWithGoogle = async () => {
  try {
    console.log('Starting Google sign-in process');
    const result = await signInWithPopup(auth, googleProvider);
    console.log('Google sign-in successful');
    await createOrUpdateUserDocument(result.user);
    return result.user;
  } catch (error: any) {
    console.error('Error signing in with Google:', error);
    
    // Add specific error handling
    if (error.code === 'auth/configuration-not-found') {
      console.error('Firebase configuration not found. Check if Google sign-in is enabled in Firebase console.');
      throw new Error('Google authentication is not properly configured. Please check your Firebase console settings.');
    }
    
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in popup was closed before completing the sign-in process.');
    }
    
    if (error.code === 'auth/popup-blocked') {
      throw new Error('Sign-in popup was blocked by the browser. Please allow popups for this site.');
    }
    
    throw error;
  }
};

export const signInWithMicrosoft = async () => {
  try {
    const result = await signInWithPopup(auth, microsoftProvider);
    await createOrUpdateUserDocument(result.user);
    return result.user;
  } catch (error) {
    console.error('Error signing in with Microsoft:', error);
    throw error;
  }
};

export const logOut = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

export const getCurrentUser = () => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe();
      if (user) {
        await createOrUpdateUserDocument(user);
      }
      resolve(user);
    });
  });
};

// Firestore helper functions for questionnaires
export const saveQuestionnaireProgress = async (userId: string, questionnaireId: string | null, data: any) => {
  try {
    const now = serverTimestamp();
    
    // If no questionnaireId is provided, create a new questionnaire
    if (!questionnaireId) {
      console.log('Firebase: Creating new questionnaire for user:', userId);
      const questionnairesRef = collection(db, 'users', userId, 'questionnaires');
      const docRef = doc(questionnairesRef);
      
      console.log('Firebase: Generated document ID:', docRef.id);
      
      await setDoc(docRef, {
        ...data,
        createdAt: now,
        updatedAt: now,
        isComplete: false,
        currentStep: data.currentStep || 0
      });
      
      console.log('Firebase: New questionnaire created with ID:', docRef.id);
      return docRef.id;
    } 
    // Otherwise, update the existing questionnaire
    else {
      console.log('Firebase: Updating existing questionnaire:', questionnaireId);
      const questionnaireRef = doc(db, 'users', userId, 'questionnaires', questionnaireId);
      
      await updateDoc(questionnaireRef, {
        ...data,
        updatedAt: now
      });
      
      console.log('Firebase: Questionnaire updated successfully');
      return questionnaireId;
    }
  } catch (error) {
    console.error('Firebase: Error saving questionnaire progress:', error);
    throw error;
  }
};

export const completeQuestionnaire = async (userId: string, questionnaireId: string, data: any) => {
  try {
    // First, save the final questionnaire data to Firestore
    console.log('Saving final questionnaire data to Firestore before completion');
    const questionnaireRef = doc(db, 'users', userId, 'questionnaires', questionnaireId);
    
    await updateDoc(questionnaireRef, {
      ...data,
      updatedAt: serverTimestamp(),
      isComplete: true,
      submittedAt: serverTimestamp(),
      templateId: data.templateId || 'default' // Ensure templateId is saved
    });
    
    console.log('Questionnaire saved to Firestore, now calling completion API for email notifications');
    
    // Then call the backend API endpoint to trigger email notifications
    const user = auth.currentUser;
    if (!user) throw new Error('User must be authenticated');
    
    const token = await user.getIdToken();
    const response = await fetch(`https://api-reuvze3ckq-uc.a.run.app/api/questionnaire-instances/${questionnaireId}/complete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        responses: data.formData,
        timeSpent: data.timeSpent || 0
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Email notification API failed:', response.status, errorText);
      // Don't throw error here - questionnaire is already completed in Firestore
    } else {
      const responseData = await response.json();
      console.log('Email notification API called successfully:', responseData);
    }
    
    return questionnaireId;
  } catch (error) {
    console.error('Error completing questionnaire:', error);
    throw error;
  }
};

export const getQuestionnaire = async (userId: string, questionnaireId: string) => {
  try {
    console.log('Firebase: Getting questionnaire', { userId, questionnaireId });
    const questionnaireRef = doc(db, 'users', userId, 'questionnaires', questionnaireId);
    const questionnaireSnap = await getDoc(questionnaireRef);
    
    console.log('Firebase: Document exists?', questionnaireSnap.exists());
    
    if (questionnaireSnap.exists()) {
      const data = questionnaireSnap.data();
      console.log('Firebase: Raw questionnaire data:', data);
      return {
        id: questionnaireSnap.id,
        formData: data.formData || {},
        currentStep: data.currentStep || 0,
        isComplete: data.isComplete || false,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : null,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : null,
        submittedAt: data.submittedAt instanceof Timestamp ? data.submittedAt.toDate() : null
      };
    }
    
    console.log('Firebase: No questionnaire found with ID:', questionnaireId);
    return null;
  } catch (error) {
    console.error('Firebase: Error getting questionnaire:', error);
    throw error;
  }
};

export const getUserQuestionnaires = async (userId: string) => {
  try {
    const questionnairesRef = collection(db, 'users', userId, 'questionnaires');
    const questionnairesSnap = await getDocs(questionnairesRef);
    
    return questionnairesSnap.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        formData: data.formData || {},
        currentStep: data.currentStep || 0,
        isComplete: data.isComplete || false,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : null,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : null,
        submittedAt: data.submittedAt instanceof Timestamp ? data.submittedAt.toDate() : null
      };
    });
  } catch (error) {
    console.error('Error getting user questionnaires:', error);
    throw error;
  }
};

// Function to delete a questionnaire
export const deleteQuestionnaireDoc = async (userId: string, questionnaireId: string) => {
  try {
    console.log('Firebase: Deleting questionnaire:', questionnaireId);
    const questionnaireRef = doc(db, 'users', userId, 'questionnaires', questionnaireId);
    await deleteDoc(questionnaireRef);
    console.log('Firebase: Questionnaire deleted successfully');
  } catch (error) {
    console.error('Firebase: Error deleting questionnaire:', error);
    throw error;
  }
};

// Function to get all completed questionnaires from all users
export const getAllCompletedQuestionnaires = async () => {
  try {
    console.log('Fetching all completed questionnaires');
    // Get all users
    const usersRef = collection(db, 'users');
    
    try {
      const usersSnap = await getDocs(usersRef);
      
      console.log(`Found ${usersSnap.docs.length} users in the database`);
      console.log('User IDs:', usersSnap.docs.map(doc => doc.id));
      
      // For each user, get their completed questionnaires
      const allQuestionnaires = [];
      
      for (const userDoc of usersSnap.docs) {
        const userId = userDoc.id;
        const userData = userDoc.data();
        console.log(`Checking questionnaires for user: ${userId}, email: ${userData.email || 'Unknown'}`);
        
        try {
          // First, list all questionnaires for this user to see what's available
          const allQuestionnairesRef = collection(db, 'users', userId, 'questionnaires');
          const allQuestionnairesSnap = await getDocs(allQuestionnairesRef);
          console.log(`User ${userId} has ${allQuestionnairesSnap.docs.length} total questionnaires`);
          
          // Log all questionnaires for this user
          if (allQuestionnairesSnap.docs.length > 0) {
            console.log(`All questionnaires for user ${userId}:`, 
              allQuestionnairesSnap.docs.map(doc => ({
                id: doc.id,
                isComplete: doc.data().isComplete,
                currentStep: doc.data().currentStep
              }))
            );
          }
          
          // Now get only completed questionnaires
          const questionnairesRef = collection(db, 'users', userId, 'questionnaires');
          const q = query(questionnairesRef, where('isComplete', '==', true));
          const questionnairesSnap = await getDocs(q);
          
          console.log(`Found ${questionnairesSnap.docs.length} completed questionnaires for user: ${userId}`);
          
          if (questionnairesSnap.docs.length > 0) {
            console.log(`Questionnaire IDs for user ${userId}:`, questionnairesSnap.docs.map(doc => doc.id));
          }
          
          const userQuestionnaires = questionnairesSnap.docs.map(doc => {
            const data = doc.data();
            console.log(`Questionnaire data for ${doc.id}:`, data);
            return {
              id: doc.id,
              userId: userId,
              userEmail: data.formData?.email || 'Unknown',
              formData: data.formData || {},
              currentStep: data.currentStep || 0,
              isComplete: data.isComplete || false,
              createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : null,
              updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : null,
              submittedAt: data.submittedAt instanceof Timestamp ? data.submittedAt.toDate() : null
            };
          });
          
          allQuestionnaires.push(...userQuestionnaires);
        } catch (error) {
          console.error(`Error getting questionnaires for user ${userId}:`, error);
          // Continue with next user
        }
      }
      
      console.log(`Total completed questionnaires found: ${allQuestionnaires.length}`);
      
      // Sort by submission date (newest first)
      return allQuestionnaires.sort((a, b) => {
        if (!a.submittedAt) return 1;
        if (!b.submittedAt) return -1;
        return b.submittedAt.getTime() - a.submittedAt.getTime();
      });
    } catch (error) {
      console.error('Error getting users collection:', error);
      return [];
    }
  } catch (error) {
    console.error('Error getting all completed questionnaires:', error);
    throw error;
  }
};

// Function to get ALL questionnaires (both complete and incomplete) from all users
export const getAllQuestionnaires = async () => {
  try {
    console.log('Fetching ALL questionnaires (complete and incomplete)');
    // Get all users
    const usersRef = collection(db, 'users');
    
    try {
      const usersSnap = await getDocs(usersRef);
      
      console.log(`Found ${usersSnap.docs.length} users in the database`);
      console.log('User IDs:', usersSnap.docs.map(doc => doc.id));
      
      // For each user, get ALL their questionnaires
      const allQuestionnaires = [];
      
      for (const userDoc of usersSnap.docs) {
        const userId = userDoc.id;
        const userData = userDoc.data();
        console.log(`Checking questionnaires for user: ${userId}, email: ${userData.email || 'Unknown'}`);
        
        try {
          // Get ALL questionnaires for this user (no filter for completion status)
          const questionnairesRef = collection(db, 'users', userId, 'questionnaires');
          const questionnairesSnap = await getDocs(questionnairesRef);
          
          console.log(`Found ${questionnairesSnap.docs.length} total questionnaires for user: ${userId}`);
          
          if (questionnairesSnap.docs.length > 0) {
            console.log(`Questionnaire IDs for user ${userId}:`, questionnairesSnap.docs.map(doc => doc.id));
          }
          
          const userQuestionnaires = questionnairesSnap.docs.map(doc => {
            const data = doc.data();
            console.log(`Questionnaire data for ${doc.id}:`, {
              id: doc.id,
              isComplete: data.isComplete,
              currentStep: data.currentStep,
              hasFormData: !!data.formData
            });
            
            // Make sure we have valid formData
            const formData = data.formData || {};
            
            // Create the questionnaire object
            return {
              id: doc.id,
              userId: userId,
              formData: formData,
              currentStep: data.currentStep || 0,
              isComplete: data.isComplete || false,
              createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
              updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(),
              submittedAt: data.submittedAt instanceof Timestamp ? data.submittedAt.toDate() : null
            };
          });
          
          allQuestionnaires.push(...userQuestionnaires);
        } catch (error) {
          console.error(`Error getting questionnaires for user ${userId}:`, error);
          // Continue with next user
        }
      }
      
      console.log(`Total questionnaires found: ${allQuestionnaires.length}`);
      console.log(`Complete questionnaires: ${allQuestionnaires.filter(q => q.isComplete).length}`);
      console.log(`Incomplete questionnaires: ${allQuestionnaires.filter(q => !q.isComplete).length}`);
      
      // Sort by date (newest first)
      const sortedQuestionnaires = allQuestionnaires.sort((a, b) => {
        const dateA = a.submittedAt || a.updatedAt || a.createdAt;
        const dateB = b.submittedAt || b.updatedAt || b.createdAt;
        return dateB.getTime() - dateA.getTime();
      });
      
      console.log(`Returning sorted questionnaires: ${sortedQuestionnaires.length}`);
      return sortedQuestionnaires;
      
    } catch (error) {
      console.error('Error getting users:', error);
      return []; // Return empty array on error
    }
  } catch (error) {
    console.error('Error in getAllQuestionnaires:', error);
    return []; // Return empty array on error
  }
};

// Function to create a test completed questionnaire for testing admin functionality
export const createTestCompletedQuestionnaire = async () => {
  try {
    console.log('Creating test completed questionnaire');
    
    // Create a test user document if it doesn't exist
    const testUserId = 'test-user-123';
    const testUserRef = doc(db, 'users', testUserId);
    
    // Check if the test user exists
    const testUserSnap = await getDoc(testUserRef);
    if (!testUserSnap.exists()) {
      await setDoc(testUserRef, {
        email: 'test@example.com',
        createdAt: serverTimestamp()
      });
      console.log('Created test user');
    }
    
    // Create a test questionnaire
    const questionnairesRef = collection(db, 'users', testUserId, 'questionnaires');
    const testQuestionnaireRef = doc(questionnairesRef);
    
    const now = new Date();
    const timestamp = Timestamp.fromDate(now);
    
    const testQuestionnaireData = {
      formData: {
        name: 'Test User',
        email: 'test@example.com',
        phone: '123-456-7890',
        company: 'Test Company',
        website: 'www.testcompany.com',
        projectType: 'Website Redesign',
        projectDescription: 'This is a test completed questionnaire',
        projectGoals: 'Testing admin functionality',
        targetAudience: 'Developers',
        timeline: '3 months',
        budget: '$10,000 - $20,000',
        launchDate: '2023-12-31',
        existingBrand: 'Yes',
        brandDescription: 'Test brand description',
        competitorWebsites: 'www.competitor1.com, www.competitor2.com',
        inspirationalWebsites: 'www.inspiration1.com, www.inspiration2.com',
        requiredFeatures: ['Contact Form', 'Blog', 'E-commerce'],
        contentManagement: 'WordPress',
        ecommerce: 'WooCommerce',
        additionalNotes: 'This is a test questionnaire for admin functionality'
      },
      currentStep: 5,
      isComplete: true,
      createdAt: timestamp,
      updatedAt: timestamp,
      submittedAt: timestamp
    };
    
    await setDoc(testQuestionnaireRef, testQuestionnaireData);
    console.log('Created test completed questionnaire with ID:', testQuestionnaireRef.id);
    
    // Verify the document was created correctly
    const verifyDoc = await getDoc(testQuestionnaireRef);
    if (verifyDoc.exists()) {
      console.log('Verification: Test questionnaire exists with data:', verifyDoc.data());
      console.log('Verification: isComplete flag is set to:', verifyDoc.data().isComplete);
    } else {
      console.error('Verification failed: Test questionnaire was not created correctly');
    }
    
    // Create a second test user with an incomplete questionnaire
    const testUser2Id = 'test-user-456';
    const testUser2Ref = doc(db, 'users', testUser2Id);
    
    // Check if the second test user exists
    const testUser2Snap = await getDoc(testUser2Ref);
    if (!testUser2Snap.exists()) {
      await setDoc(testUser2Ref, {
        email: 'test2@example.com',
        createdAt: serverTimestamp()
      });
      console.log('Created second test user');
    }
    
    // Create an incomplete questionnaire for the second test user
    const questionnaires2Ref = collection(db, 'users', testUser2Id, 'questionnaires');
    const testQuestionnaire2Ref = doc(questionnaires2Ref);
    
    const incompleteQuestionnaireData = {
      formData: {
        name: 'Test User 2',
        email: 'test2@example.com',
        phone: '987-654-3210',
        company: 'Another Test Company',
        website: 'www.anothertest.com',
        projectType: 'Mobile App'
      },
      currentStep: 2,
      isComplete: false,
      createdAt: timestamp,
      updatedAt: timestamp,
      submittedAt: null
    };
    
    await setDoc(testQuestionnaire2Ref, incompleteQuestionnaireData);
    console.log('Created test incomplete questionnaire with ID:', testQuestionnaire2Ref.id);
    
    return {
      userId1: testUserId,
      questionnaireId1: testQuestionnaireRef.id,
      userId2: testUser2Id,
      questionnaireId2: testQuestionnaire2Ref.id
    };
  } catch (error) {
    console.error('Error creating test completed questionnaire:', error);
    throw error;
  }
};

// Function to check for all questionnaires in the database (both complete and incomplete)
export const checkForCompletedQuestionnaires = async () => {
  try {
    console.log('Checking for all questionnaires in the database');
    
    // Get all users
    const usersRef = collection(db, 'users');
    const usersSnap = await getDocs(usersRef);
    
    console.log(`Found ${usersSnap.docs.length} users in the database`);
    
    // Check each user for all questionnaires
    let totalCompleted = 0;
    let totalIncomplete = 0;
    const userSummary = [];
    
    for (const userDoc of usersSnap.docs) {
      const userId = userDoc.id;
      const userData = userDoc.data();
      
      // Get all questionnaires for this user
      const questionnairesRef = collection(db, 'users', userId, 'questionnaires');
      const questionnairesSnap = await getDocs(questionnairesRef);
      
      // Count completed and incomplete questionnaires
      const completedCount = questionnairesSnap.docs.filter(doc => {
        const data = doc.data();
        return data.isComplete === true;
      }).length;
      
      const incompleteCount = questionnairesSnap.docs.filter(doc => {
        const data = doc.data();
        return data.isComplete !== true;
      }).length;
      
      totalCompleted += completedCount;
      totalIncomplete += incompleteCount;
      
      userSummary.push({
        userId,
        email: userData.email || 'Unknown',
        totalQuestionnaires: questionnairesSnap.docs.length,
        completedQuestionnaires: completedCount,
        incompleteQuestionnaires: incompleteCount
      });
    }
    
    console.log('User summary:', userSummary);
    console.log(`Total completed questionnaires across all users: ${totalCompleted}`);
    console.log(`Total incomplete questionnaires across all users: ${totalIncomplete}`);
    
    return {
      totalUsers: usersSnap.docs.length,
      totalCompleted,
      totalIncomplete,
      userSummary
    };
  } catch (error) {
    console.error('Error checking for completed questionnaires:', error);
    throw error;
  }
};

export { auth, db, googleProvider, microsoftProvider }; 